import type { Post } from '$lib/api/rpc';

/**
 * @description Sorts pinned posts in ascending order of offset value,
 * and if offset value is the same we order based post's created_at, where
 * newer posts of same offset value are placed before older posts.
 */
const getSortedPinnedPosts = (pinnedPosts: Post[]) => {
	return pinnedPosts.sort((a, b) => {
		const offsetDiff = (a.offset || 0) - (b.offset || 0);

		if (offsetDiff === 0) {
			return (b.created_at ?? '').localeCompare(a.created_at ?? '');
		}

		return offsetDiff;
	});
};

const getOffsetValueToPinnedPostsMap = (pinnedPosts: Post[]): Map<number, Post[]> => {
	return pinnedPosts.reduce((acc, post) => {
		const offset = post.offset ?? 0;

		if (!acc.has(offset)) {
			acc.set(offset, []);
		}

		acc.get(offset)?.push(post);

		return acc;
	}, new Map<number, Post[]>());
};

/**
 * @description We have seen many cases where the offset value of a pinned post is not unique
 * amongst the set of pinned posts. We use a map to ensure all pinned posts are in the stream
 * in the case of duplicate offsets.
 */
const insertPinnedPostsIntoStream = (streamPosts: Post[], pinnedPosts: Post[]) => {
	const [...streamPostsCopy] = streamPosts;

	const offsetValueToPinnedPostMap = getOffsetValueToPinnedPostsMap(pinnedPosts);

	for (const [offset, posts] of offsetValueToPinnedPostMap) {
		if (offset < streamPostsCopy.length) {
			// Splice in all pinned posts with the same offset value starting at that index in the stream
			streamPostsCopy.splice(offset, 0, ...posts);
		} else {
			// If offset value is larger than number of posts in the stream, push to the end of the stream
			streamPostsCopy.push(...posts);
		}
	}

	return streamPostsCopy;
};

export function getPostsForCurrentStream(
	allPosts: Post[],
	featuredPosts: Post[],
	pinnedPosts: Post[]
) {
	const featuredPostIds = featuredPosts.map((post) => post.id);
	const pinnedPostIds = pinnedPosts.map((post) => post.id);

	// remove pinned posts that are also featured so the don't appear twice
	const nonFeaturedPinnedPosts = getSortedPinnedPosts(pinnedPosts).filter(
		(post) => !featuredPostIds.includes(post.id)
	);

	const postIdsToFilterOut = [...featuredPostIds, ...pinnedPostIds];

	// filter out all the pinned and featured posts from allPosts array
	const nonPinnedOrFeaturedStreamPosts = allPosts.filter((post) => {
		return !postIdsToFilterOut.includes(post.id);
	});

	return insertPinnedPostsIntoStream(nonPinnedOrFeaturedStreamPosts, nonFeaturedPinnedPosts);
}
